/**
 * The non-privileged read view of a Provider Interface (ADR-040 §D-10;
 * REQ-UI-165; Flow V §7).
 *
 * What an `operator` or `analyst` sees: **name, type, state, trust mode,
 * transport security, conversion mode, connection health and a filter summary**
 * — and nothing else, because bind endpoints, demarcation IPs, cert-material
 * references, the conversion profile and filter *literals* describe collection
 * posture and stay `admin` / `support_engineer` only.
 *
 * **The BFF already withheld those fields.** This component does not re-apply
 * the rule; it renders what arrived. A privileged session viewing without
 * editing sees the same summary with the editor a click away — the split is in
 * the response, not in this render path.
 *
 * # Why any of it is visible at all
 *
 * These are the flows *entering* the system. An operator who cannot see them
 * cannot notice one that has stopped — and on ingress that matters more than on
 * egress: a stopped egress interface backs up and is eventually noticed
 * downstream, while a stopped ingress interface is simply **quiet**, and quiet is
 * what a healthy night shift looks like (Flow V §2).
 *
 * Cross-references:
 *   - ADR-040 §D-10 (read field-set); REQ-UI-165.
 *   - REQ-UI-160 (the filter summary's inverted reading), -161, -162.
 *   - Flow V §7; Flow L §11 (a silenced flow is never hidden).
 */
import { resolveEffectiveState, type ChainLink } from "@/peers/peer-effective-state";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";
import { PeerSection } from "@/peers/peer-section";
import { StatePill } from "@/peers/peer-state-controls";
import {
  findProviderConnectionRole,
  findProviderInterfaceType,
  findProviderTransportSecurity,
  findTrustMode,
} from "@/providers/provider-catalog";
import { describeFilterSummaryReading } from "@/providers/provider-filter-tree";
import type { ProviderInterfaceView } from "@/providers/provider-model";

/** Props for {@link ProviderInterfaceSummary}. */
export interface ProviderInterfaceSummaryProps {
  view: ProviderInterfaceView;
  /**
   * The owning chain, root-first — ``[provider]`` when mounted in the dialog.
   *
   * A non-privileged reader gets the ancestor annotation too: every state it is
   * derived from is in the all-roles field-set, and an operator who cannot see
   * that a provider is holding an interface down is exactly the reader who most
   * needs to (Flow V §5a).
   */
  ancestors?: readonly ChainLink[];
}

/** Render the field-limited read view of one provider interface. */
export function ProviderInterfaceSummary({
  view,
  ancestors = [],
}: ProviderInterfaceSummaryProps): React.ReactElement {
  const descriptor = findProviderInterfaceType(view.type_id);
  const chain: readonly ChainLink[] = [...ancestors, { name: view.name, state: view.state }];
  // No scroller of its own — the dialog is one scrollable pane (ADR-024 §4).
  return (
    <div data-testid="provider-interface-summary">
      <PeerSection id="provider-identity" title="Interface">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm">
          <dt className="text-text-secondary">Name</dt>
          <dd className="text-text-primary">{view.name}</dd>
          <dt className="text-text-secondary">Type</dt>
          <dd className="text-text-primary">{descriptor?.label ?? view.type_id}</dd>
          {/* All three are in the ALL-ROLES field-set and are read here for the
              reason this whole view exists: they are the facts a non-privileged
              reader most needs — who authenticated the counterparty, whether
              mission data arrives in the clear, and which decoder produced the
              record. All three are postures, so none discloses an address, a
              credential path or a filter literal, which is what this view
              withholds. The conversion PROFILE and the demarcation ADDRESSES are
              deliberately absent: they name specific deployment shape. */}
          <dt className="text-text-secondary">Trust mode</dt>
          <dd className="text-text-primary" data-testid="provider-summary-trust-mode">
            {findTrustMode(view.trust_mode)?.label ?? view.trust_mode}
          </dd>
          <dt className="text-text-secondary">Transport security</dt>
          <dd className="text-text-primary" data-testid="provider-summary-transport-security">
            {findProviderTransportSecurity(view.transport_security)?.label ??
              view.transport_security}
          </dd>
          <dt className="text-text-secondary">Conversion</dt>
          <dd className="text-text-primary" data-testid="provider-summary-conversion-mode">
            {view.conversion_mode === "engine"
              ? "Engine — the installed conversion profile is authoritative"
              : "Legacy — the built-in decoder is authoritative"}
          </dd>
          <dt className="text-text-secondary">Loaded</dt>
          <dd className="text-text-primary">{view.enabled ? "Enabled" : "Disabled"}</dd>
          <dt className="text-text-secondary">State</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <StatePill state={view.state} />
            <PeerSuppressionNote
              resolved={resolveEffectiveState(ancestors, view.state)}
              testId="provider-summary-interface"
            />
          </dd>
        </dl>
        {/* The delegation is stated to a non-privileged reader too. They cannot
            change it, which is exactly why they need to SEE it — under `tunnel`
            a compromised upstream can claim any producer id, and a reader who
            cannot tell a tunnel-attributed feed from an authenticated one cannot
            judge what the `producer_id` on a record is worth
            (REQ-PROVIDER-032). */}
        {view.trust_mode === "tunnel" && (
          <p
            role="status"
            data-testid="provider-summary-tunnel-delegation"
            className="mt-2 rounded border border-warning px-2 py-1 text-meta text-warning"
          >
            {findTrustMode("tunnel")?.delegation}
          </p>
        )}
        {view.transport_security === "plaintext" && (
          <p
            role="status"
            data-testid="provider-summary-plaintext-consequence"
            className="mt-2 rounded border border-destructive px-2 py-1 text-meta text-destructive"
          >
            {findProviderTransportSecurity("plaintext")?.consequence}
          </p>
        )}
      </PeerSection>

      <PeerSection id="provider-connections" title="Provider connections">
        {view.connections.length === 0 ? (
          <p className="text-body-sm text-text-muted" data-testid="provider-summary-no-connections">
            No connections. This interface is not listening anywhere and receives nothing.
          </p>
        ) : (
          <ul className="flex flex-col gap-1" data-testid="provider-summary-connections">
            {view.connections.map((connection) => (
              <li key={connection.connection_id} className="flex items-center gap-2 text-body-sm">
                <span className="min-w-0 flex-1 truncate text-text-primary">{connection.name}</span>
                <span className="text-meta text-text-secondary">
                  {findProviderConnectionRole(connection.role)?.label ?? connection.role}
                </span>
                <StatePill state={connection.state} />
                <PeerSuppressionNote
                  resolved={resolveEffectiveState(chain, connection.state)}
                  testId={`provider-summary-connection-${connection.name}`}
                />
              </li>
            ))}
          </ul>
        )}
      </PeerSection>

      <PeerSection id="provider-filter" title="Filter">
        <FilterSummaryBody view={view} />
      </PeerSection>
    </div>
  );
}

/** The non-disclosing filter description — axes, not values (REQ-UI-160). */
function FilterSummaryBody({ view }: { view: ProviderInterfaceView }): React.ReactElement {
  const summary = view.filter_summary;
  if (!summary.configured) {
    return (
      // Neutral, not amber: on ingress an unconfigured filter is the FAIL-SAFE
      // state. Painting it as a warning — the reading the egress twin correctly
      // uses — would tell an operator the safe default is a fault.
      <p
        className="text-body-sm text-text-secondary"
        data-testid="provider-summary-filter-unconfigured"
      >
        {describeFilterSummaryReading(false)} Filters narrow intake; unconfigured is not unfiltered
        in reverse — it means nothing is being excluded.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1" data-testid="provider-summary-filter">
      <p className="text-body-sm text-text-primary">
        {summary.clause_count} {summary.clause_count === 1 ? "condition" : "conditions"} across{" "}
        {summary.selector_kinds.length}{" "}
        {summary.selector_kinds.length === 1 ? "selector" : "selectors"}. Anything this filter
        excludes is not stored and is not recoverable.
      </p>
      <ul className="flex flex-wrap gap-1">
        {summary.selector_kinds.map((kind) => (
          <li
            key={kind}
            className="rounded border border-border bg-bg-elevated px-1.5 py-0.5 font-mono text-meta text-text-secondary"
          >
            {kind}
          </li>
        ))}
      </ul>
      <p className="text-meta text-text-muted">{describeFilterSummaryReading(true)}</p>
    </div>
  );
}
